import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { adaptApiError, Nullable } from '@pin-up-global/common';
import { HttpClient } from '@angular/common/http';
import { AccessData, Enable2FAData, SigninWithCodeData } from '@core/auth';
import { createHttpContext } from '@shared/utils';
import {
  DISABLE_API_ERRORS_INTERCEPTOR,
  DISABLE_AUTH_TOKEN_INTERCEPTOR,
  DISABLE_REFRESH_TOKEN_INTERCEPTOR,
} from '@core/interceptors';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpApiService {
  #httpClient = inject(HttpClient);

  refresh(token: Nullable<string>): Observable<Omit<AccessData, 'refresh'>> {
    if (!token) {
      return throwError(() => new Error('No refresh token'));
    }

    const context = createHttpContext([
      [DISABLE_REFRESH_TOKEN_INTERCEPTOR, true],
      [DISABLE_AUTH_TOKEN_INTERCEPTOR, true],
    ]);

    return this.#httpClient.post<Omit<AccessData, 'refresh'>>(
        `@api/auth/refresh/`,
        { refresh: token },
        { context },
    )
  }

  signIn(token: string): Observable<SigninWithCodeData> {
    const context = createHttpContext([
      [DISABLE_API_ERRORS_INTERCEPTOR, true],
      [DISABLE_AUTH_TOKEN_INTERCEPTOR, true],
      [DISABLE_REFRESH_TOKEN_INTERCEPTOR, true],
    ]);

    return this.#httpClient
               .post<SigninWithCodeData>(
                   `@api/auth/signin/`,
                   { token },
                   { context },
               )
               .pipe(
                   adaptApiError(),
               );
  }

  enable2fa(token: string): Observable<Enable2FAData> {
    const context = createHttpContext([
      [DISABLE_API_ERRORS_INTERCEPTOR, true],
      [DISABLE_AUTH_TOKEN_INTERCEPTOR, true],
      [DISABLE_REFRESH_TOKEN_INTERCEPTOR, true],
    ]);

    return this.#httpClient
               .post<Enable2FAData>(
                   `@api/auth/enable/`,
                   { token },
                   { context },
               )
               .pipe(
                   adaptApiError(),
               );
  }

  verifyCode(token: string, code: string): Observable<AccessData> {
    const context = createHttpContext([
      [DISABLE_API_ERRORS_INTERCEPTOR, true],
      [DISABLE_AUTH_TOKEN_INTERCEPTOR, true],
      [DISABLE_REFRESH_TOKEN_INTERCEPTOR, true],
    ]);

    return this.#httpClient
               .post<AccessData>(
                   `@api/auth/verify/`,
                   { token, code },
                   { context },
               )
               .pipe(
                   adaptApiError(),
               );
  }

  resetCode(token: string, code: string): Observable<AccessData> {
    const context = createHttpContext([
      [DISABLE_API_ERRORS_INTERCEPTOR, true],
      [DISABLE_AUTH_TOKEN_INTERCEPTOR, true],
      [DISABLE_REFRESH_TOKEN_INTERCEPTOR, true],
    ]);

    return this.#httpClient
               .post<AccessData>(
                   `@api/auth/reset/`,
                   { token, code },
                   { context },
               )
               .pipe(
                   adaptApiError(),
               );
  }

  getServerTime(): Observable<number> {
    const context = createHttpContext([
      [DISABLE_AUTH_TOKEN_INTERCEPTOR, true],
      [DISABLE_REFRESH_TOKEN_INTERCEPTOR, true],
    ]);

    return this.#httpClient
               .get('@api/auth/healthcheck/', {
                 observe: 'response',
                 context,
               })
               .pipe(
                   map(response => {
                     const serverTime = response.headers.get('Date');
                     return serverTime ? Date.parse(serverTime) : Date.now();
                   }),
                   catchError(() => of(Date.now())),
               );
  }

  remove2fa(): Observable<void> {
    return this.#httpClient.delete<void>(`@api/auth/remove/`);
  }
}
